import { IcoBuilding16, IcoCrosshair16, IcoEye16, IcoPin16, IcoShield16 } from '@onefootprint/icons';
import type { AgentTemplateKind } from '@onefootprint/request-types/dashboard';
import type { TFunction } from 'i18next';
import type { AskPercyTemplate } from './templates';

const AGENT_TEMPLATE_IDS = [
  'person_screening_review',
  'business_screening_review',
  'address_validation',
  'name_matching',
  'enhanced_due_diligence',
] as const;

// Whether POST /org/agents/builder builds a curated graph for each template kind. Exhaustive
// over AgentTemplateKind (via `satisfies`) so adding a backend kind fails typecheck here until
// it's classified: `true` creates the agent straight from the template, `false` is left for
// Percy to author conversationally.
const TEMPLATE_BACKED = {
  person_screening_review: true,
  business_screening_review: true,
  business_edd: true,
  person_cdd: true,
  transaction_alert_review: true,
  business_research: true,
  suspicious_activity_investigation: true,
  merchant_onboarding_review: true,
  kyc_exception_review: true,
  beneficial_ownership_verification: true,
  chargeback_dispute_review: true,
  synthetic_identity_screening: true,
  supporting_document_review: true,
  supplier_onboarding_verification: true,
  address_validation: false,
  name_matching: false,
  agentic_onboarding: false,
  custom: false,
} as const satisfies Record<AgentTemplateKind, boolean>;

export type TemplateBackedAgentId = {
  [K in keyof typeof TEMPLATE_BACKED]: (typeof TEMPLATE_BACKED)[K] extends true ? K : never;
}[keyof typeof TEMPLATE_BACKED];
export const isTemplateBackedAgentId = (id: string): id is TemplateBackedAgentId =>
  id in TEMPLATE_BACKED && TEMPLATE_BACKED[id as keyof typeof TEMPLATE_BACKED] === true;

const AGENT_TEMPLATE_ICONS = {
  person_screening_review: IcoEye16,
  business_screening_review: IcoBuilding16,
  address_validation: IcoPin16,
  name_matching: IcoCrosshair16,
  enhanced_due_diligence: IcoShield16,
} as const;

export const buildAgentTemplates = (t: TFunction<'ask-percy'>): AskPercyTemplate[] =>
  AGENT_TEMPLATE_IDS.map(id => ({
    id,
    icon: AGENT_TEMPLATE_ICONS[id],
    title: t($ => $.actionPills.agentTemplates[id].title),
    prompt: t($ => $.actionPills.agentTemplates[id].prompt),
  }));
